<template>
  <b-modal :id="id" :title="title" size="xl" centered lazy header-bg-variant="info" footer-border-variant="info" :hide-footer="true">
    <b-container>
      <div v-if="loading" class="mb-5 pb-5">
        <loading-animation />
      </div>

      <div v-if="!loading && (!items || !items.length)">
        <div class="row bg-light py-3">
          <div class="col text-center">
            Nenhum histórico encontrado
          </div>
        </div>
      </div>

      <div v-if="!loading && items && items.length">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="{ 'bg-light': index % 2 === 0 }"
          class="row py-2 mx-0 border"
        >
          <div class="col-12">
            <div class="row align-items-center">
              <div class="col-auto">
                <span class="font-weight-bold">Data: </span>{{ item.formattedCreatedAt }}
              </div>
              <div v-if="item.system" class="col-auto">
                <span class="font-weight-bold">Sistema: </span>
                <span class="badge badge-secondary">{{ item.system }}</span>
              </div>
              <div v-if="item.user" class="col-auto">
                <span class="font-weight-bold">Usuário: </span>{{ item.user.ref }} - {{ item.user.name }}
              </div>
              <div class="col-auto ml-auto">
                <b-button size="sm" variant="primary" @click="toggleJson(index)">
                  <i class="fa" :class="expandedJson[index] ? 'fa-caret-up' : 'fa-caret-down'"></i>
                  Detalhes
                </b-button>
              </div>
            </div>

            <div v-if="item.changes && Object.keys(item.changes).length" class="mt-2">
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Valor anterior</th>
                    <th>Valor novo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(field, fi) in Object.keys(item.changes)" :key="fi">
                    <td>{{ field }}</td>
                    <td>{{ item.before ? formatValue(item.before[field], field) : '-' }}</td>
                    <td>{{ item.after ? formatValue(item.after[field], field) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="mt-2 text-muted small">
              Sem detalhes de alterações registrados
            </div>

            <div v-if="expandedJson[index]" class="mt-2">
              <div class="row">
                <div class="col-6">
                  <p class="font-weight-bold mb-1">Antes</p>
                  <json-viewer
                    v-if="item.before"
                    :key="'before-' + index + '-' + expandedAll[index]"
                    :value="item.before"
                    :expand-depth="expandedAll[index] ? 999 : 1"
                    :expanded="expandedAll[index]"
                    :copyable="{ copyText: 'Copiar', copiedText: 'Copiado!' }"
                    boxed
                  />
                  <span v-else>-</span>
                </div>
                <div class="col-6">
                  <p class="font-weight-bold mb-1">Depois</p>
                  <json-viewer
                    v-if="item.after"
                    :key="'after-' + index + '-' + expandedAll[index]"
                    :value="item.after"
                    :expand-depth="expandedAll[index] ? 999 : 1"
                    :expanded="expandedAll[index]"
                    :copyable="{ copyText: 'Copiar', copiedText: 'Copiado!' }"
                    boxed
                  />
                  <span v-else>-</span>
                </div>
              </div>
              <div class="mt-2 text-center">
                <b-button size="sm" variant="secondary" @click="toggleExpandAll(index)">
                  <i class="fa" :class="expandedAll[index] ? 'fa-compress' : 'fa-expand'"></i>
                  {{ expandedAll[index] ? 'Recolher' : 'Expandir' }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <nav class="mt-3">
          <b-pagination
            v-if="totalRows"
            align="center"
            :limit="5"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Anterior"
            next-text="Próximo"
            v-model="currentPage"
            @change="pageChanged"
            :disabled="loading"
          />
        </nav>
      </div>
      <div v-if="parameters" class="mt-3 pt-2 border-top text-center">
        <i class="fa mr-1"
          :class="parameters.entityChangeHistoryEnabled ? 'fa-check-circle text-success' : 'fa-exclamation-triangle text-warning'"
        ></i>
        Histórico de alterações está <strong>{{ parameters.entityChangeHistoryEnabled ? 'ativo' : 'inativo' }}</strong> nos parâmetros
      </div>
    </b-container>
  </b-modal>
</template>

<script>
  import { httpClient } from '@/service';
  import { LoadingAnimation } from '@/components/loaders';
  import shared from '@/shared/shared';
  import JsonViewer from 'vue-json-viewer';

  export default {
    name: 'entity-change-history-modal',
    components: { LoadingAnimation, JsonViewer },

    props: {
      id: {
        type: String,
        require
      },
      title: {
        type: String,
        require
      },
      uuid: {
        type: String
      },
      tableName: {
        type: String
      },
      parameters: {
        type: Object
      }
    },

    data() {
      return {
        items: [],
        loading: false,
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        expandedJson: [],
        expandedAll: []
      }
    },

    methods: {
      pageChanged(page) {
        this.currentPage = page;
        this.load();
      },

      load() {
        if (!this.uuid) return;

        let conditions = [
          { field: 'uuid', value: this.uuid }
        ];

        if (this.tableName) {
          conditions.push({ field: 'tableName', value: this.tableName });
        }

        this.loading = true;
        this.items = [];
        this.expandedJson = [];
        this.expandedAll = [];

        httpClient.post(
          `${process.env.VUE_APP_API_URL}entity-change-history/condition?page=${this.currentPage - 1}&size=${this.perPage}&sort=createdAt,desc`,
          { conditions }
        ).then(data => {
          this.loading = false;
          this.totalRows = data.data.data.totalElements;
          this.currentPage = data.data.data.number + 1;
          this.items = data.data.data.content;

          this.items.forEach(item => {
            if (item.createdAt) {
              item.formattedCreatedAt = shared.formatDate(item.createdAt);
            }

            if (!item.changes || !Object.keys(item.changes).length) {
              item.changes = this.inferChanges(item.before, item.after);
            }
          });

          this.expandedJson = this.items.map(() => false);
          this.expandedAll = this.items.map(() => false);
        }).catch(error => {
          this.loading = false;
          if (error.message) {
            this.$notify.error(error);
          } else {
            this.$notify.textError('Houve um erro ao buscar o histórico');
          }
        });
      },

      inferChanges(before, after) {
        const result = {};
        const isSimple = v => v === null || v === undefined || (typeof v !== 'object' && !Array.isArray(v));
        const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);

        allKeys.forEach(key => {
          const prev = before ? before[key] : undefined;
          const next = after ? after[key] : undefined;

          if (isSimple(prev) && isSimple(next) && prev !== next) {
            result[key] = next;
          }
        });

        return result;
      },

      toggleJson(index) {
        this.$set(this.expandedJson, index, !this.expandedJson[index]);
      },

      toggleExpandAll(index) {
        this.$set(this.expandedAll, index, !this.expandedAll[index]);
      },

      formatValue(value, field) {
        if (field && value && typeof value === 'number' && /date|Date|At|at|Data|data/i.test(field)) {
          return shared.formatDate(value);
        }
        return value;
      }
    }
  }
</script>

<style scoped>
</style>
